import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  ParticipantRole,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { FieldType, InputFormatType } from '@common/enums';
import { getDarwinFieldMappings } from '@common/external/darwin/darwin-field-mappings';
import { DarwinEnabledNormalizedFields } from '@common/external/darwin/darwin.fields';
import { ValueType } from '@common/graphql';
import { formatToCurrency } from '@common/utils/formatter';
import dayjs from 'dayjs';
import { TriangleAlert } from 'lucide-react';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DarwinPropertyWithParticipants,
  PropertyOption,
} from '~/apps/extension/pages/Compliance/context/hooks/useDarwinSync';
import { Select } from '~/services/document/common/Select';

/**
 * Builds a composite key from a key term and Darwin field key.
 * Format: "keyTerm:darwinKey" (e.g., "purchase_price:sellingPrice")
 */
const buildCompositeKey = (keyTerm: string, darwinKey: string): string => {
  return `${keyTerm}:${darwinKey}`;
};

/**
 * Splits a composite key into its constituent parts.
 * @param compositeKey - The composite key in format "keyTerm:darwinKey"
 * @returns Object containing keyTerm and darwinKey
 */
const splitCompositeKey = (
  compositeKey: string
): { keyTerm: string; darwinKey: string } => {
  const [keyTerm, darwinKey] = compositeKey.split(':');

  return { keyTerm, darwinKey };
};

/**
 * Default display value for missing or null data.
 */
const DEFAULT_DISPLAY_VALUE = '—';

/**
 * Converts a value to a number or returns null if conversion fails.
 * Handles both numeric and string inputs safely.
 */
const toNumber = (value: ValueType | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

/**
 * Commission field groups that define relationships between percentage and amount fields.
 * Used to derive one value from the other when combined with purchase price.
 * NOTE: When both percentage and amount are present, percentage takes precedence.
 */
const COMMISSION_FIELD_GROUPS: Array<{
  percentKey: DarwinEnabledNormalizedFields;
  amountKey: DarwinEnabledNormalizedFields;
}> = [
  {
    percentKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER_PERCENT,
    amountKey:
      DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER,
  },
  {
    percentKey:
      DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION_PERCENT,
    amountKey: DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION,
  },
];

export interface ParticipantInfo {
  label: string;
  value: string;
  role: ParticipantRole;
}

interface DarwinUploadPopoverProps {
  children: ReactNode;
  onUpload?: (
    selectedFields: string[],
    selectedParticipants: ParticipantRole[],
    selectedPropertyId?: string | null
  ) => Promise<void>;
  keyTermValues?: Record<string, ValueType>;
  participants?: ParticipantInfo[];
  darwinPropertyLoading: boolean;
  darwinProperties?: DarwinPropertyWithParticipants[] | undefined;
  selectedProperty?: PropertyOption | null;
  onPropertyChange?: (property: PropertyOption | null) => void;
  propertyOptions: PropertyOption[];
  transactionSide?: TransactionSide | null;
}

export const DarwinUploadPopover: FC<DarwinUploadPopoverProps> = ({
  children,
  onUpload,
  keyTermValues,
  participants = [],
  darwinPropertyLoading,
  darwinProperties,
  selectedProperty,
  onPropertyChange,
  propertyOptions,
  transactionSide,
}) => {
  const visibleKeyTermKeys = useMemo(() => {
    return ((values: Record<string, ValueType> | undefined): string[] => {
      if (!values) {
        return [];
      }

      return Object.keys(values);
    })(keyTermValues);
  }, [keyTermValues]);

  // Memoize field mappings to avoid repeated calls to getDarwinFieldMappings
  const fieldMappingsCache = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof getDarwinFieldMappings>>();
    visibleKeyTermKeys.forEach((fieldKey) => {
      const mappings = getDarwinFieldMappings(
        fieldKey as DarwinEnabledNormalizedFields,
        transactionSide
      );
      cache.set(fieldKey, mappings);
    });

    return cache;
  }, [visibleKeyTermKeys, transactionSide]);

  // Compute allowed composite keys based on visible fields and transaction side
  const allowedCompositeKeys = useMemo(() => {
    if (!keyTermValues) {
      return new Set<string>();
    }

    const keys = new Set<string>();

    visibleKeyTermKeys.forEach((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return;
      }

      fieldConfigs.forEach((config) => {
        keys.add(buildCompositeKey(fieldKey, config.darwinKey));
      });
    });

    return keys;
  }, [keyTermValues, visibleKeyTermKeys, fieldMappingsCache]);

  // Track previous allowed keys to identify newly allowed keys
  const previousAllowedKeysRef = useRef<Set<string>>(new Set());

  // Store both key term field and darwin field key as "keyTerm:darwinKey"
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    if (!keyTermValues) return [];

    // Select all visible Darwin fields by default
    return visibleKeyTermKeys.flatMap((fieldKey) => {
      const fieldConfigs = fieldMappingsCache.get(fieldKey);

      if (!fieldConfigs || fieldConfigs.length === 0) {
        return [];
      }

      return fieldConfigs.map((config) =>
        buildCompositeKey(fieldKey, config.darwinKey)
      );
    });
  });
  const [selectedParticipants, setSelectedParticipants] = useState<
    ParticipantRole[]
  >(() => {
    // Select all participants by default
    return participants.map((p) => p.role);
  });
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Update selected fields when allowed keys change
  useEffect(() => {
    if (allowedCompositeKeys.size === 0) {
      setSelectedFields([]);
      previousAllowedKeysRef.current = new Set();

      return;
    }

    const previousAllowedKeys = previousAllowedKeysRef.current;
    const newlyAllowedKeys = Array.from(allowedCompositeKeys).filter(
      (key) => !previousAllowedKeys.has(key)
    );
    previousAllowedKeysRef.current = new Set(allowedCompositeKeys);

    setSelectedFields((prev) => {
      // Filter out keys that are no longer allowed
      const filtered = prev.filter((compositeKey) =>
        allowedCompositeKeys.has(compositeKey)
      );

      // Add newly allowed keys
      const next = [...filtered];
      newlyAllowedKeys.forEach((key) => {
        if (!next.includes(key)) {
          next.push(key);
        }
      });

      // Return previous state if no changes to avoid unnecessary re-renders
      if (
        next.length === prev.length &&
        next.every((key, index) => key === prev[index])
      ) {
        return prev;
      }

      return next;
    });
  }, [allowedCompositeKeys]);

  const options: PropertyOption[] = useMemo(() => {
    return [
      {
        value: '',
        label: 'Create Property Record',
      },
      ...propertyOptions,
    ];
  }, [propertyOptions]);

  const formatValueByType = (
    value: ValueType,
    fieldType: FieldType,
    inputFormat?: { type: InputFormatType }
  ) => {
    if (value === null || value === undefined) {
      return DEFAULT_DISPLAY_VALUE;
    }

    switch (fieldType) {
      case FieldType.NUMBER:
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return String(value);
        }

        if (inputFormat?.type === InputFormatType.CURRENCY) {
          return formatToCurrency(numericValue);
        }

        if (inputFormat?.type === InputFormatType.PERCENTAGE) {
          return `${numericValue.toFixed(2)}%`;
        }

        return new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
        }).format(numericValue);
      case FieldType.DATE:
        const parsed = dayjs(String(value));

        return parsed.isValid()
          ? parsed.format('MMM D, YYYY')
          : DEFAULT_DISPLAY_VALUE;
      default:
        return String(value);
    }
  };

  const getFieldComparisons = (fieldKey: DarwinEnabledNormalizedFields) => {
    const fieldConfigs = fieldMappingsCache.get(fieldKey);
    if (!fieldConfigs || fieldConfigs.length === 0 || !keyTermValues) {
      return [];
    }

    // Find the selected Darwin property
    const selectedDarwinProperty =
      darwinProperties?.find(
        (p) => p.propertyId.toString() === selectedProperty?.value
      ) ?? null;

    let keyTermValue = keyTermValues[fieldKey];

    const commissionGroup = COMMISSION_FIELD_GROUPS.find(
      (group) => group.percentKey === fieldKey || group.amountKey === fieldKey
    );

    if (commissionGroup) {
      const percentValue = toNumber(
        keyTermValues?.[commissionGroup.percentKey]
      );
      const amountValue = toNumber(keyTermValues?.[commissionGroup.amountKey]);
      const purchasePriceValue = toNumber(
        keyTermValues?.[DarwinEnabledNormalizedFields.PURCHASE_PRICE]
      );

      // Derive percentage from amount if percentage is missing
      if (
        commissionGroup.percentKey === fieldKey &&
        percentValue === null &&
        amountValue !== null &&
        purchasePriceValue !== null &&
        purchasePriceValue !== 0
      ) {
        keyTermValue = (amountValue / purchasePriceValue) * 100;
      }

      // Derive or override amount from percentage
      // NOTE: Percentage always takes precedence when both are present
      if (
        commissionGroup.amountKey === fieldKey &&
        percentValue !== null &&
        purchasePriceValue !== null
      ) {
        keyTermValue = (percentValue / 100) * purchasePriceValue;
      }
    }

    // Create a comparison for each Darwin field config
    return fieldConfigs.map((config) => {
      const formattedKeyTermValue = config.fieldType
        ? formatValueByType(keyTermValue, config.fieldType, config.inputFormat)
        : keyTermValue;

      // Use the label from the config instead of deriving from darwinKey
      const displayName =
        config.label ||
        config.darwinKey
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();

      if (!selectedDarwinProperty) {
        // No property selected or creating new property
        return {
          darwinKey: config.darwinKey,
          hasChange: true,
          keyTermValue: formattedKeyTermValue,
          darwinValue: DEFAULT_DISPLAY_VALUE,
          displayName,
        };
      }

      const darwinValue: unknown =
        selectedDarwinProperty[
          config.darwinKey as keyof DarwinPropertyWithParticipants
        ];

      const adjustedDarwinValue =
        config.fieldType === FieldType.NUMBER &&
        config.inputFormat?.type === InputFormatType.PERCENTAGE &&
        typeof darwinValue === 'number'
          ? darwinValue * 100
          : darwinValue;

      const formattedDarwinValue = config.fieldType
        ? formatValueByType(
            adjustedDarwinValue as ValueType,
            config.fieldType,
            config.inputFormat
          )
        : adjustedDarwinValue;

      const hasChange = formattedKeyTermValue !== formattedDarwinValue;

      return {
        darwinKey: config.darwinKey,
        hasChange,
        keyTermValue: formattedKeyTermValue,
        darwinValue: formattedDarwinValue as
          | string
          | number
          | boolean
          | string[]
          | undefined,
        displayName,
      };
    });
  };

  const getParticipantComparison = (
    participant: ParticipantInfo
  ): { hasChange: boolean; participantValue: string; darwinValue: string } => {
    // Find the selected Darwin property
    const selectedDarwinProperty =
      darwinProperties?.find(
        (p) => p.propertyId.toString() === selectedProperty?.value
      ) ?? null;

    const participantValue = participant.value;

    if (!selectedDarwinProperty || !selectedDarwinProperty.participants) {
      // No property selected or creating new property or no participants data
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Map participant role to Darwin participants array
    let darwinParticipants:
      | Array<{
          personName: string;
        }>
      | undefined;

    if (participant.role === ParticipantRole.BUYERS_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.buyersAgents ?? [];
    } else if (participant.role === ParticipantRole.LISTING_AGENT) {
      darwinParticipants =
        selectedDarwinProperty.participants.listingAgents ?? [];
    } else if (participant.role === ParticipantRole.BUYER) {
      darwinParticipants = selectedDarwinProperty.participants.buyers ?? [];
    } else if (participant.role === ParticipantRole.SELLER) {
      darwinParticipants = selectedDarwinProperty.participants.sellers ?? [];
    }
    // Add other role mappings here as they become supported

    if (!darwinParticipants || darwinParticipants.length === 0) {
      return {
        hasChange: true,
        participantValue,
        darwinValue: DEFAULT_DISPLAY_VALUE,
      };
    }

    // Join Darwin participant names for comparison
    const darwinValue = darwinParticipants.map((p) => p.personName).join(', ');

    const hasChange = participantValue !== darwinValue;

    return {
      hasChange,
      participantValue,
      darwinValue,
    };
  };

  const handleFieldToggle = (compositeKey: string) => {
    // Extract the key term from the composite key (e.g., "purchase_price:sellingPrice" -> "purchase_price")
    const { keyTerm } = splitCompositeKey(compositeKey);

    // Find all Darwin fields for this key term
    const fieldConfigs = fieldMappingsCache.get(keyTerm);

    if (!fieldConfigs || fieldConfigs.length === 0) {
      return;
    }

    // Create composite keys for all Darwin fields of this key term
    const allCompositeKeys = fieldConfigs.map((config) =>
      buildCompositeKey(keyTerm, config.darwinKey)
    );

    setSelectedFields((prev) => {
      // Check if any of the related fields are currently selected
      const isAnySelected = allCompositeKeys.some((key) => prev.includes(key));

      if (isAnySelected) {
        // Uncheck all related fields
        return prev.filter((key) => !allCompositeKeys.includes(key));
      } else {
        // Check all related fields
        return [...prev, ...allCompositeKeys];
      }
    });
  };

  const handleParticipantToggle = (role: ParticipantRole) => {
    setSelectedParticipants((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleUpload = async () => {
    if (
      !onUpload ||
      (selectedFields.length === 0 && selectedParticipants.length === 0)
    ) {
      return;
    }

    setIsUploading(true);
    try {
      const propertyId = selectedProperty?.value || null;

      // Extract unique key term fields from composite keys (keyTerm:darwinKey)
      // If any Darwin field for a key term is selected, include that key term
      const keyTermFields = Array.from(
        new Set(
          selectedFields.map(
            (compositeKey) => splitCompositeKey(compositeKey).keyTerm
          )
        )
      );

      await onUpload(keyTermFields, selectedParticipants, propertyId);
      onClose();
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.';

      toast({
        title: 'Failed to upload to Darwin',
        status: 'error',
        description,
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      lazyBehavior="unmount"
      size="md"
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="compliance.tooltip.bg"
          border="1px solid"
          borderColor="compliance.border.primary"
          boxShadow="lg"
          maxW="350px"
        >
          <PopoverCloseButton color="compliance.text.secondary" py={5} />
          <PopoverHeader py={3}>
            Choose which fields to write to Darwin
          </PopoverHeader>
          <PopoverBody fontWeight="normal" p={4} textTransform="none">
            <VStack align="stretch" spacing={4}>
              {!darwinPropertyLoading && darwinProperties?.length === 0 && (
                <Box
                  border="1px solid"
                  borderColor="yellow.500"
                  borderRadius="md"
                  p={3}
                >
                  <Flex align="center" gap={3}>
                    <Icon as={TriangleAlert} color="yellow.500" size={20} />
                    <Text color="yellow.500">
                      We couldn't find a property record, a new one will be
                      created.
                    </Text>
                  </Flex>
                </Box>
              )}

              <Select<PropertyOption>
                isDisabled={options.length <= 1}
                options={options}
                placeholder="Choose a property..."
                value={selectedProperty}
                onChange={onPropertyChange}
              />

              {visibleKeyTermKeys.length > 0 && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    color="compliance.text.primary"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Purchase Details
                  </Text>
                  {visibleKeyTermKeys.flatMap((fieldKey) => {
                    const comparisons = getFieldComparisons(
                      fieldKey as DarwinEnabledNormalizedFields
                    );

                    // Create a separate checkbox for each Darwin field
                    return comparisons.map((comparison) => {
                      // Composite key to uniquely identify each Darwin field
                      const compositeKey = buildCompositeKey(
                        fieldKey,
                        comparison.darwinKey
                      );

                      return (
                        <Flex
                          key={compositeKey}
                          align="center"
                          justify="space-between"
                          w="100%"
                        >
                          <Checkbox
                            colorScheme="blue"
                            isChecked={selectedFields.includes(compositeKey)}
                            onChange={() => handleFieldToggle(compositeKey)}
                          >
                            <Text
                              color="compliance.text.muted"
                              fontSize="sm"
                              textTransform="none"
                            >
                              {comparison.displayName}
                            </Text>
                          </Checkbox>

                          <Text
                            color="compliance.text.muted"
                            fontSize="xs"
                            textAlign="right"
                          >
                            {comparison.hasChange ? (
                              <>
                                <Text
                                  as="s"
                                  color="compliance.text.muted"
                                  display="inline"
                                >
                                  {String(
                                    comparison.darwinValue ||
                                      DEFAULT_DISPLAY_VALUE
                                  )}
                                </Text>{' '}
                                →{' '}
                                <Text as="b" display="inline">
                                  {comparison.keyTermValue}
                                </Text>
                              </>
                            ) : (
                              <Text display="inline">
                                {comparison.keyTermValue} → No change
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      );
                    });
                  })}
                </VStack>
              )}

              {participants.length > 0 && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    color="compliance.text.primary"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Participants
                  </Text>
                  {participants.map((participant) => {
                    const comparison = getParticipantComparison(participant);

                    return (
                      <Flex
                        key={participant.role}
                        align="center"
                        justify="space-between"
                        w="100%"
                      >
                        <Checkbox
                          colorScheme="blue"
                          isChecked={selectedParticipants.includes(
                            participant.role
                          )}
                          onChange={() =>
                            handleParticipantToggle(participant.role)
                          }
                        >
                          <Text
                            color="compliance.text.muted"
                            fontSize="sm"
                            textTransform="none"
                          >
                            {participant.label}
                          </Text>
                        </Checkbox>

                        <Text
                          color="compliance.text.muted"
                          fontSize="xs"
                          textAlign="right"
                        >
                          {comparison.hasChange ? (
                            <>
                              <Text
                                as="s"
                                color="compliance.text.muted"
                                display="inline"
                              >
                                {comparison.darwinValue ||
                                  DEFAULT_DISPLAY_VALUE}
                              </Text>{' '}
                              →{' '}
                              <Text as="b" display="inline">
                                {comparison.participantValue}
                              </Text>
                            </>
                          ) : (
                            <Text display="inline">
                              {comparison.participantValue} → No change
                            </Text>
                          )}
                        </Text>
                      </Flex>
                    );
                  })}
                </VStack>
              )}

              <Button
                isDisabled={
                  (selectedFields.length === 0 &&
                    selectedParticipants.length === 0) ||
                  !onUpload
                }
                isLoading={isUploading}
                loadingText="Writing to Darwin..."
                size="sm"
                onClick={() => void handleUpload()}
              >
                Write to Darwin (
                {selectedFields.length + selectedParticipants.length} items)
              </Button>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
